// ScopeFields — the scope editor's sections (ADR-050 §3), the Scope part of the
// Access body: Sites — "All", or the places grammar (`PlacesFields`: the
// zones-and-clusters tree and the extra sites, a covered site said and dropped
// by the piece); Depots, Vehicle pools and Teams (team scope — which teams a team key opens, never a
// membership; ADR-083 §4) — "All", or the small list. Every "All" switch is enabled only where the
// viewer holds all of that kind (the read's `giver`), and the LAST one — the
// whole project — only for a project-wide viewer; the grants outside the viewer's
// reach are a COUNT under the section, kept untouched by the save (ADR-050 §1).
// "Copy scope from" a person the viewer manages fills the sections with what the
// viewer may grant of theirs and counts the rest. A CONTROLLED, presentational
// component over one `ScopeFormValues` — each host maps its form (watch +
// setValue) onto value/onChange. The pickers load on the press, never for a
// closed drawer (the family's rule).

import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProjectUserDetail } from '@ems-app/schemas/users/responses';
import { Picker } from '../../components/patterns/Picker.js';
import { PlacesFields } from '../../components/patterns/PlacesFields.js';
import { Toggle } from '../../components/ui/Toggle.js';
import { usePersonFace } from '../../components/patterns/lookupFaces.js';
import { depotsSource } from '../../hooks/useWarehouses.js';
import { vehiclePoolsSource } from '../../hooks/useVehiclePools.js';
import { teamNamesSource } from '../../hooks/useTeams.js';
import { personSource } from '../../hooks/useUsers.js';
import { apiClient } from '../../lib/apiClient.js';
import { copyScope, NO_OUTSIDE, type ScopeFormValues, type ScopeGiver, type ScopeOutside } from './scope.js';

export interface ScopeFieldsProps {
  projectId: string;
  value: ScopeFormValues;
  onChange: (next: ScopeFormValues) => void;
  /** What the viewer may switch to "All" (the read's `giver`; a new person: the viewer's own standing). */
  giver: ScopeGiver;
  /** The grants outside the viewer's reach, kept — a count per section (a new person has none). */
  outside?: ScopeOutside;
  /** The vehicles module is on — the Vehicle pools section exists. */
  vehicles: boolean;
  /** The Teams section exists — the field-force module is on AND the role carries a team key or the team level
   *  (ADR-083 §11); the host decides. */
  teams?: boolean;
  disabled?: boolean;
}

type Kind = 'sites' | 'depots' | 'vehicle_pools' | 'teams';

function Section({
  title,
  allLabel,
  all,
  canAll,
  onAll,
  outside,
  disabled,
  children,
}: {
  title: string;
  allLabel: string;
  all: boolean;
  canAll: boolean;
  onAll: (on: boolean) => void;
  outside: number;
  disabled: boolean;
  children: React.ReactNode;
}) {
  const { t } = useTranslation('users');
  return (
    <fieldset className="rounded-lg border border-[var(--color-border)] p-3">
      <legend className="sr-only">{title}</legend>
      <div className="flex items-center justify-between gap-3">
        <span aria-hidden className="text-sm font-medium text-[var(--color-text-primary)]">
          {title}
        </span>
        {/* An "All" the viewer may not grant is read, not edited: the switch stays as it is, disabled. */}
        <Toggle checked={all} onChange={onAll} label={allLabel} disabled={disabled || !canAll} />
      </div>
      {!all && <div className="mt-3 space-y-3">{children}</div>}
      {outside > 0 && <p className="mt-2 text-xs text-[var(--color-text-secondary)]">{t('scope.outsideKept', { count: outside })}</p>}
    </fieldset>
  );
}

export function ScopeFields({ projectId, value, onChange, giver, outside = NO_OUTSIDE, vehicles, teams = false, disabled = false }: ScopeFieldsProps) {
  const { t } = useTranslation('users');
  const personFace = usePersonFace();
  const depots = useMemo(() => depotsSource(projectId), [projectId]);
  const pools = useMemo(() => vehiclePoolsSource(projectId), [projectId]);
  // The teams a giver may grant — the ones inside their own team scope (ADR-050 §3's bound applied to the third twin).
  const grantableTeams = useMemo(() => teamNamesSource(projectId, 'grantable'), [projectId]);
  // The people the viewer manages (ADR-050 §3 — the whom-bound; the read of a person outside it is refused).
  const people = useMemo(() => personSource(projectId, 'managed', personFace), [projectId, personFace]);

  const [copyFromId, setCopyFromId] = useState<string | null>(null);
  const [copyNote, setCopyNote] = useState<{ name: string; notCopied: number } | { failed: true } | null>(null);
  const copyAsk = useRef(0);
  const copyFromPerson = async (id: string | null) => {
    setCopyFromId(id);
    const ask = ++copyAsk.current;
    if (id === null) {
      setCopyNote(null);
      return;
    }
    // Asked on the pick, applied once: the read names only what the viewer may grant of that person's scope. A reply
    // to an earlier pick is dropped; a refused read clears the pick and says so.
    try {
      const r = await apiClient.get<ProjectUserDetail>(`/projects/${projectId}/users/${id}`);
      if (ask !== copyAsk.current) return;
      const { values, notCopied } = copyScope(r.data.scope, giver);
      onChange(values);
      setCopyNote({ name: `${r.data.first_name} ${r.data.last_name}`, notCopied });
    } catch {
      if (ask !== copyAsk.current) return;
      setCopyFromId(null);
      setCopyNote({ failed: true });
    }
  };

  const sites = value.sites;
  // An "All" is switched on only where the viewer holds all of that kind — and the LAST one, which would make the
  // whole project, only by a project-wide viewer (the server stores the whole project as one state).
  const poolsAll = !vehicles || value.vehicle_pools.all;
  const teamsAll = !teams || value.teams.all;
  const canAll = (kind: Kind, all: boolean, othersAll: boolean) => giver[kind] && (all || giver.whole || !othersAll);
  return (
    <div className="space-y-3">
      <Section
        title={t('scope.sections.sites')}
        allLabel={t('scope.all')}
        all={sites.all}
        canAll={canAll('sites', sites.all, value.depots.all && poolsAll && teamsAll)}
        onAll={(on) => onChange({ ...value, sites: { ...sites, all: on } })}
        outside={outside.sites}
        disabled={disabled}
      >
        <PlacesFields projectId={projectId} value={sites} onChange={(places) => onChange({ ...value, sites: { ...sites, ...places } })} disabled={disabled} />
      </Section>

      <Section
        title={t('scope.sections.depots')}
        allLabel={t('scope.all')}
        all={value.depots.all}
        canAll={canAll('depots', value.depots.all, sites.all && poolsAll && teamsAll)}
        onAll={(on) => onChange({ ...value, depots: { ...value.depots, all: on } })}
        outside={outside.depots}
        disabled={disabled}
      >
        <Picker
          multiple
          selectedDisplay="tags"
          source={depots}
          label={t('scope.sections.depots')}
          placeholder={t('common:places.depotsPlaceholder')}
          value={value.depots.ids}
          onChange={(next) => onChange({ ...value, depots: { ...value.depots, ids: next } })}
          disabled={disabled}
        />
      </Section>

      {vehicles && (
        <Section
          title={t('scope.sections.pools')}
          allLabel={t('scope.all')}
          all={value.vehicle_pools.all}
          canAll={canAll('vehicle_pools', value.vehicle_pools.all, sites.all && value.depots.all && teamsAll)}
          onAll={(on) => onChange({ ...value, vehicle_pools: { ...value.vehicle_pools, all: on } })}
          outside={outside.vehicle_pools}
          disabled={disabled}
        >
          <Picker
            multiple
            selectedDisplay="tags"
            source={pools}
            label={t('scope.sections.pools')}
            placeholder={t('common:places.poolsPlaceholder')}
            value={value.vehicle_pools.ids}
            onChange={(next) => onChange({ ...value, vehicle_pools: { ...value.vehicle_pools, ids: next } })}
            disabled={disabled}
          />
        </Section>
      )}

      {teams && (
        <Section
          title={t('scope.sections.teams')}
          allLabel={t('scope.all')}
          all={value.teams.all}
          canAll={canAll('teams', value.teams.all, sites.all && value.depots.all && poolsAll)}
          onAll={(on) => onChange({ ...value, teams: { ...value.teams, all: on } })}
          outside={outside.teams}
          disabled={disabled}
        >
          <Picker
            multiple
            selectedDisplay="tags"
            source={grantableTeams}
            label={t('scope.sections.teams')}
            placeholder={t('scope.teamsPlaceholder')}
            value={value.teams.ids}
            onChange={(next) => onChange({ ...value, teams: { ...value.teams, ids: next } })}
            disabled={disabled}
          />
        </Section>
      )}

      <div>
        <Picker
          source={people}
          label={t('scope.copyFrom')}
          placeholder={t('scope.copyFromPlaceholder')}
          clearable
          value={copyFromId}
          onChange={(id) => void copyFromPerson(id)}
          disabled={disabled}
        />
        {copyNote && (
          <p className="mt-1 text-xs text-[var(--color-text-secondary)]" role="status">
            {'failed' in copyNote ? t('scope.copyFailed') : `${t('scope.copied', { name: copyNote.name })}${copyNote.notCopied > 0 ? ` · ${t('scope.notCopied', { count: copyNote.notCopied })}` : ''}`}
          </p>
        )}
      </div>
    </div>
  );
}
